import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Globe, Plus, Copy, Trash2, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Custom Domains Section — Phase 4 Tier B.
 * Renders inside the Branding tab (Agency/Pro plan only).
 * Lets the agency add a domain, shows DNS instructions, and verifies TXT record.
 */
export default function CustomDomainsSection({ api, workspaceId }) {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);

  const fetchDomains = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/custom-domains`);
      setDomains(res.data?.domains || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDomains(); /* eslint-disable-next-line */ }, [workspaceId]);

  const addDomain = async (e) => {
    e.preventDefault();
    const dom = newDomain.trim().toLowerCase();
    if (!dom) return;
    setAdding(true);
    try {
      await api.post(`/api/workspaces/${workspaceId}/custom-domains`, { workspace_id: workspaceId, domain: dom });
      toast.success('Domain added — now configure DNS and click Verify');
      setNewDomain('');
      fetchDomains();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add domain');
    } finally { setAdding(false); }
  };

  const verify = async (domainId) => {
    setVerifyingId(domainId);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/custom-domains/${domainId}/verify`);
      if (res.data?.verified) toast.success('Domain verified 🎉');
      else toast.error('Verification failed — TXT record not found yet. DNS can take up to 30 min.');
      fetchDomains();
    } catch { toast.error('Verification failed'); }
    finally { setVerifyingId(null); }
  };

  const remove = async (domainId, dom) => {
    if (!window.confirm(`Remove ${dom}?`)) return;
    try {
      await api.delete(`/api/workspaces/${workspaceId}/custom-domains/${domainId}`);
      toast.success('Domain removed');
      fetchDomains();
    } catch { toast.error('Failed to remove'); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || 'Copied'}`);
  };

  return (
    <div className="mt-8 pt-6 border-t border-white/[0.06]" data-testid="custom-domains-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-teal-400/10 flex items-center justify-center">
          <Globe className="text-teal-400" size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-white">Custom Domain</h3>
          <p className="text-[11px] text-gray-400">Let clients access the portal at your own domain (e.g. <code>app.youragency.com</code>).</p>
        </div>
      </div>

      <form onSubmit={addDomain} className="flex gap-2 mb-4">
        <input
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="app.youragency.com"
          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-teal-400/50 focus:outline-none text-sm"
          data-testid="custom-domain-input"
        />
        <button
          type="submit" disabled={adding}
          className="px-4 py-2 rounded-lg bg-teal-400/15 hover:bg-teal-400/25 text-teal-300 text-sm font-semibold inline-flex items-center gap-1.5 border border-teal-400/30 disabled:opacity-60"
          data-testid="custom-domain-add-btn"
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>

      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : domains.length === 0 ? (
        <p className="text-xs text-gray-500">No custom domains yet. Add one above to whitelabel your portal.</p>
      ) : (
        <div className="space-y-3">
          {domains.map((d) => (
            <div key={d.domain_id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={`custom-domain-card-${d.domain_id}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <code className="text-sm text-white font-medium">{d.domain}</code>
                  {d.verified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-400/15 text-emerald-300 border border-emerald-400/25">
                      <CheckCircle2 size={10} /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/15 text-amber-300 border border-amber-400/25">
                      <AlertCircle size={10} /> Pending DNS
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {!d.verified && (
                    <button
                      onClick={() => verify(d.domain_id)} disabled={verifyingId === d.domain_id}
                      className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white text-xs font-medium disabled:opacity-60"
                      data-testid={`custom-domain-verify-${d.domain_id}`}
                    >
                      {verifyingId === d.domain_id ? 'Checking…' : 'Verify'}
                    </button>
                  )}
                  <button
                    onClick={() => remove(d.domain_id, d.domain)}
                    className="p-1.5 rounded-md text-rose-400/80 hover:text-rose-400 hover:bg-rose-400/10"
                    data-testid={`custom-domain-remove-${d.domain_id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {!d.verified && (
                <div className="space-y-3 text-[11px]">
                  <p className="text-gray-400">Add these two DNS records at your DNS provider (Cloudflare, GoDaddy, Namecheap, Route 53, etc.), then click <b className="text-white">Verify</b>.</p>

                  {/* Column headers — same labels every DNS provider uses */}
                  <div className="grid grid-cols-[70px_1fr_1fr_60px_32px] gap-2 px-3 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                    <div>Type</div>
                    <div>Host / Name</div>
                    <div>Value / Points to</div>
                    <div>TTL</div>
                    <div></div>
                  </div>

                  {/* CNAME record */}
                  {(() => {
                    const cnameHost = d.domain;
                    const cnameValue = d.cname_target || 'cname.voxinity.ai';
                    return (
                      <div className="grid grid-cols-[70px_1fr_1fr_60px_32px] gap-2 items-center px-3 py-2.5 rounded-lg bg-black/30 border border-white/[0.06]">
                        <span className="px-2 py-0.5 rounded bg-teal-400/15 text-[10px] font-bold text-teal-300 text-center w-fit">CNAME</span>
                        <code className="text-gray-200 truncate font-mono cursor-pointer hover:text-white" onClick={() => copy(cnameHost, 'Host copied')} title={cnameHost}>{cnameHost}</code>
                        <code className="text-gray-200 truncate font-mono cursor-pointer hover:text-white" onClick={() => copy(cnameValue, 'Value copied')} title={cnameValue}>{cnameValue}</code>
                        <span className="text-gray-400 font-mono">Auto</span>
                        <button
                          onClick={() => copy(cnameValue, 'CNAME value copied')}
                          className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white"
                          data-testid={`custom-domain-copy-cname-${d.domain_id}`}
                          title="Copy value"
                        ><Copy size={12} /></button>
                      </div>
                    );
                  })()}

                  {/* TXT record */}
                  {(() => {
                    const txtHost = d.verification_host || `_voxinity-verify.${d.domain}`;
                    const txtValue = d.verification_token;
                    return (
                      <div className="grid grid-cols-[70px_1fr_1fr_60px_32px] gap-2 items-center px-3 py-2.5 rounded-lg bg-black/30 border border-white/[0.06]">
                        <span className="px-2 py-0.5 rounded bg-violet-400/15 text-[10px] font-bold text-violet-300 text-center w-fit">TXT</span>
                        <code className="text-gray-200 truncate font-mono cursor-pointer hover:text-white" onClick={() => copy(txtHost, 'Host copied')} title={txtHost}>{txtHost}</code>
                        <code className="text-gray-200 truncate font-mono cursor-pointer hover:text-white" onClick={() => copy(txtValue, 'Value copied')} title={txtValue}>{txtValue}</code>
                        <span className="text-gray-400 font-mono">Auto</span>
                        <button
                          onClick={() => copy(txtValue, 'TXT value copied')}
                          className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white"
                          data-testid={`custom-domain-copy-txt-${d.domain_id}`}
                          title="Copy value"
                        ><Copy size={12} /></button>
                      </div>
                    );
                  })()}

                  <p className="text-[10px] text-gray-500 leading-relaxed">
                    <b className="text-gray-400">Tip:</b> Some providers only want the part before your domain (e.g. just <code className="text-gray-300">app</code> instead of <code className="text-gray-300">app.tecartemis.com</code>). Click any field to copy it.
                  </p>

                  {(d.hostname_status || d.ssl_status) && (
                    <div className="flex items-center gap-4 pt-1">
                      <span className="text-[10px] text-gray-500">Ownership: <b className={`${d.hostname_status === 'active' ? 'text-emerald-400' : 'text-amber-300'}`}>{d.hostname_status || 'pending'}</b></span>
                      <span className="text-[10px] text-gray-500">SSL: <b className={`${d.ssl_status === 'active' ? 'text-emerald-400' : 'text-amber-300'}`}>{d.ssl_status || 'pending'}</b></span>
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500">DNS can take 2–30 minutes to propagate. SSL is issued automatically once both records verify.</p>
                </div>
              )}
              {d.verified && (
                <p className="text-[11px] text-gray-500">
                  Clients can now sign in at <a href={`https://${d.domain}/l/`} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline">https://{d.domain}/l/&lt;sub&gt;</a>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
